import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RefundDetailsModal from './components/RefundDetailsModal';
import ApprovalActionModal from './components/ApprovalActionModal';
import BulkApprovalModal from './components/BulkApprovalModal';

const RefundApprovalWorkflow = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRefunds, setSelectedRefunds] = useState([]);
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, refund: null });
  const [actionModal, setActionModal] = useState({ isOpen: false, refund: null, action: null });
  const [bulkModal, setBulkModal] = useState({ isOpen: false });

  const [refundRequests, setRefundRequests] = useState([
    {
      id: 1,
      requestId: 'REF-2026-001',
      customerName: 'Acme Corp',
      customerEmail: 'contact@acmecorp.com',
      refundAmount: 14999,
      originalAmount: 14999,
      transactionId: 'TXN-20260205-001',
      reasonCode: 'service_issue',
      reason: 'Service not meeting expectations',
      requestDate: '2026-02-08T10:30:00',
      priority: 'high',
      status: 'pending_cs',
      approvalLevel: 'customer_service',
      communicationHistory: [
        { date: '2026-02-08T10:30:00', type: 'request', message: 'Customer submitted refund request' }
      ],
      attachments: ['complaint_screenshot.png'],
      disputeDocumentation: 'Customer reported multiple service outages'
    },
    {
      id: 2,
      requestId: 'REF-2026-002',
      customerName: 'Tech Solutions Ltd',
      customerEmail: 'billing@techsolutions.com',
      refundAmount: 2999,
      originalAmount: 5999,
      transactionId: 'TXN-20260204-002',
      reasonCode: 'duplicate_payment',
      reason: 'Duplicate charge detected',
      requestDate: '2026-02-07T14:20:00',
      priority: 'medium',
      status: 'pending_finance',
      approvalLevel: 'finance',
      communicationHistory: [
        { date: '2026-02-07T14:20:00', type: 'request', message: 'Customer submitted refund request' },
        { date: '2026-02-07T15:00:00', type: 'approval', message: 'Approved by Customer Service' }
      ],
      attachments: ['bank_statement.pdf'],
      disputeDocumentation: 'Duplicate transaction confirmed'
    },
    {
      id: 3,
      requestId: 'REF-2026-003',
      customerName: 'StartupXYZ',
      customerEmail: 'admin@startupxyz.com',
      refundAmount: 2999,
      originalAmount: 2999,
      transactionId: 'TXN-20260203-003',
      reasonCode: 'billing_error',
      reason: 'Incorrect plan charged',
      requestDate: '2026-02-06T09:15:00',
      priority: 'low',
      status: 'approved',
      approvalLevel: 'completed',
      communicationHistory: [
        { date: '2026-02-06T09:15:00', type: 'request', message: 'Customer submitted refund request' },
        { date: '2026-02-06T10:00:00', type: 'approval', message: 'Approved by Customer Service' },
        { date: '2026-02-06T11:30:00', type: 'approval', message: 'Approved by Finance' },
        { date: '2026-02-06T12:00:00', type: 'processed', message: 'Refund processed successfully' }
      ],
      attachments: [],
      disputeDocumentation: 'Plan upgrade error confirmed'
    }
  ]);

  const handleSelectRefund = (refundId) => {
    setSelectedRefunds(prev => 
      prev?.includes(refundId) 
        ? prev?.filter(id => id !== refundId)
        : [...prev, refundId]
    );
  };

  const filteredRequests = refundRequests?.filter(request => {
    const matchesSearch = request?.customerName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         request?.requestId?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || request?.priority === priorityFilter;
    const matchesStatus = statusFilter === 'all' || request?.status === statusFilter;
    return matchesSearch && matchesPriority && matchesStatus;
  });

  const handleSelectAll = () => {
    if (selectedRefunds?.length === filteredRequests?.length) {
      setSelectedRefunds([]);
    } else {
      setSelectedRefunds(filteredRequests?.map(r => r?.id));
    }
  };

  const handleViewDetails = (refund) => {
    setDetailsModal({ isOpen: true, refund });
  };

  const handleAction = (refund, action) => {
    setActionModal({ isOpen: true, refund, action });
  };

  const handleBulkApproval = () => {
    setBulkModal({ isOpen: true });
  };

  const confirmAction = (action, amount, reason, notes) => {
    const refund = actionModal?.refund;
    setRefundRequests(refundRequests?.map(r => {
      if (r?.id === refund?.id) {
        const newHistory = [...r?.communicationHistory, {
          date: new Date()?.toISOString(),
          type: action === 'reject' ? 'rejection' : 'approval',
          message: `${action === 'reject' ? 'Rejected' : 'Approved'} - ${notes || reason}`
        }];
        return {
          ...r,
          status: action === 'reject' ? 'rejected' : (action === 'approve_full' ? 'approved' : 'partial_approved'),
          refundAmount: amount || r?.refundAmount,
          communicationHistory: newHistory
        };
      }
      return r;
    }));
    setActionModal({ isOpen: false, refund: null, action: null });
  };

  const confirmBulkApproval = () => {
    setRefundRequests(refundRequests?.map(r => {
      if (selectedRefunds?.includes(r?.id) && r?.status === 'pending_cs') {
        return {
          ...r,
          status: 'approved',
          communicationHistory: [...r?.communicationHistory, {
            date: new Date()?.toISOString(),
            type: 'approval',
            message: 'Bulk approved'
          }]
        };
      }
      return r;
    }));
    setSelectedRefunds([]);
    setBulkModal({ isOpen: false });
  };

  const getPriorityBadge = (priority) => {
    const config = {
      high: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'High', icon: 'AlertCircle' },
      medium: { bg: 'bg-warning/10', text: 'text-warning', label: 'Medium', icon: 'AlertTriangle' },
      low: { bg: 'bg-success/10', text: 'text-success', label: 'Low', icon: 'CheckCircle' }
    };
    const priorityConfig = config?.[priority] || config?.low;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${priorityConfig?.bg} ${priorityConfig?.text}`}>
        <Icon name={priorityConfig?.icon} size={12} />
        {priorityConfig?.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      pending_cs: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending CS' },
      pending_finance: { bg: 'bg-info/10', text: 'text-info', label: 'Pending Finance' },
      pending_management: { bg: 'bg-primary/10', text: 'text-primary', label: 'Pending Mgmt' },
      approved: { bg: 'bg-success/10', text: 'text-success', label: 'Approved' },
      rejected: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Rejected' },
      partial_approved: { bg: 'bg-info/10', text: 'text-info', label: 'Partial' }
    };
    const statusConfig = config?.[status] || config?.pending_cs;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.text}`}>
        {statusConfig?.label}
      </span>
    );
  };

  const analytics = {
    totalRequests: refundRequests?.length,
    pendingApproval: refundRequests?.filter(r => r?.status?.includes('pending'))?.length,
    approved: refundRequests?.filter(r => r?.status === 'approved')?.length,
    avgProcessingTime: '2.5 hours',
    totalRefundAmount: refundRequests?.reduce((sum, r) => sum + r?.refundAmount, 0)
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
              <Icon name="RotateCcw" size={32} className="text-primary" />
              Refund Approval Workflow
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
              Manage refund requests through structured approval chains
            </p>
          </div>

          {/* Analytics Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Requests</p>
                  <p className="text-xl font-bold text-foreground">{analytics?.totalRequests}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-xl font-bold text-foreground">{analytics?.pendingApproval}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Approved</p>
                  <p className="text-xl font-bold text-foreground">{analytics?.approved}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center">
                  <Icon name="Timer" size={20} className="text-info" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Avg Time</p>
                  <p className="text-lg font-bold text-foreground">{analytics?.avgProcessingTime}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <Icon name="DollarSign" size={20} className="text-destructive" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Amount</p>
                  <p className="text-lg font-bold text-foreground">₹{analytics?.totalRefundAmount?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters & Bulk Actions */}
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search customer or request ID..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Priorities</option>
                <option value="high">High Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="low">Low Priority</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Status</option>
                <option value="pending_cs">Pending CS</option>
                <option value="pending_finance">Pending Finance</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <Button
                variant="default"
                size="md"
                onClick={handleBulkApproval}
                disabled={selectedRefunds?.length === 0}
                iconName="CheckCircle"
                iconPosition="left"
              >
                Bulk Approve ({selectedRefunds?.length})
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRefunds?.length === filteredRequests?.length && filteredRequests?.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-border"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Request ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests?.map((request) => (
                  <tr key={request?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedRefunds?.includes(request?.id)}
                        onChange={() => handleSelectRefund(request?.id)}
                        className="w-4 h-4 rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-4 font-mono text-sm font-semibold text-foreground">{request?.requestId}</td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-foreground">{request?.customerName}</p>
                        <p className="text-xs text-muted-foreground">{request?.customerEmail}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 font-semibold text-foreground">₹{request?.refundAmount?.toLocaleString()}</td>
                    <td className="px-4 py-4 text-sm text-foreground">{request?.reason}</td>
                    <td className="px-4 py-4">{getPriorityBadge(request?.priority)}</td>
                    <td className="px-4 py-4">{getStatusBadge(request?.status)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(request)}
                          className="p-2 hover:bg-muted rounded-md transition-smooth"
                          title="View Details"
                        >
                          <Icon name="Eye" size={16} className="text-primary" />
                        </button>
                        {request?.status?.includes('pending') && (
                          <>
                            <button
                              onClick={() => handleAction(request, 'approve_full')}
                              className="p-2 hover:bg-success/10 rounded-md transition-smooth"
                              title="Approve Full Refund"
                            >
                              <Icon name="CheckCircle" size={16} className="text-success" />
                            </button>
                            <button
                              onClick={() => handleAction(request, 'partial')}
                              className="p-2 hover:bg-info/10 rounded-md transition-smooth"
                              title="Partial Refund"
                            >
                              <Icon name="DollarSign" size={16} className="text-info" />
                            </button>
                            <button
                              onClick={() => handleAction(request, 'reject')}
                              className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                              title="Reject"
                            >
                              <Icon name="XCircle" size={16} className="text-destructive" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {filteredRequests?.map((request) => (
              <div key={request?.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedRefunds?.includes(request?.id)}
                      onChange={() => handleSelectRefund(request?.id)}
                      className="w-4 h-4 rounded border-border mt-1"
                    />
                    <div>
                      <p className="font-mono text-sm font-semibold text-foreground">{request?.requestId}</p>
                      <p className="text-sm font-medium text-foreground mt-1">{request?.customerName}</p>
                      <p className="text-xs text-muted-foreground">{request?.customerEmail}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {getPriorityBadge(request?.priority)}
                    {getStatusBadge(request?.status)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Refund Amount</p>
                    <p className="text-sm font-semibold text-foreground">₹{request?.refundAmount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Request Date</p>
                    <p className="text-sm text-foreground">{new Date(request.requestDate)?.toLocaleDateString()}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Reason</p>
                    <p className="text-sm text-foreground">{request?.reason}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(request)}
                    iconName="Eye"
                    iconPosition="left"
                    fullWidth
                  >
                    Details
                  </Button>
                  {request?.status?.includes('pending') && (
                    <>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleAction(request, 'approve_full')}
                        iconName="CheckCircle"
                        iconPosition="left"
                        fullWidth
                      >
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleAction(request, 'reject')}
                        iconName="XCircle"
                        iconPosition="left"
                        fullWidth
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Modals */}
      <RefundDetailsModal
        isOpen={detailsModal?.isOpen}
        refund={detailsModal?.refund}
        onClose={() => setDetailsModal({ isOpen: false, refund: null })}
      />
      <ApprovalActionModal
        isOpen={actionModal?.isOpen}
        refund={actionModal?.refund}
        action={actionModal?.action}
        onClose={() => setActionModal({ isOpen: false, refund: null, action: null })}
        onConfirm={confirmAction}
      />
      <BulkApprovalModal
        isOpen={bulkModal?.isOpen}
        selectedCount={selectedRefunds?.length}
        onClose={() => setBulkModal({ isOpen: false })}
        onConfirm={confirmBulkApproval}
      />
    </div>
  );
};

export default RefundApprovalWorkflow;